import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { ErrorDisplay } from './ErrorDisplay';

interface CryptoSettings {
  hashAlgorithm: string;
  jsonCanonizationStrict: boolean;
  ecdhCurve: string;
  nonceTtlMs: number;
  timestampWindowMs: number;
}

const DEFAULT_SETTINGS: CryptoSettings = {
  hashAlgorithm: 'sha256',
  jsonCanonizationStrict: true,
  ecdhCurve: 'secp256k1',
  nonceTtlMs: 3600000, // 1 hour
  timestampWindowMs: 300000, // 5 minutes
};

const STORAGE_KEY = 'userwallet_crypto_settings';

export function CryptoSettingsScreen(): JSX.Element {
  const navigate = useNavigate();
  const { error, handleError, clearError } = useErrorHandler();
  const [settings, setSettings] = useState<CryptoSettings>(DEFAULT_SETTINGS);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      try {
        const parsed = JSON.parse(stored) as CryptoSettings;
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      } catch {
        // Ignore invalid stored settings
      }
    }
  }, []);

  const handleSave = (): void => {
    setIsSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      clearError();
    } catch (err) {
      handleError(err, 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = (): void => {
    setSettings(DEFAULT_SETTINGS);
    localStorage.removeItem(STORAGE_KEY);
    clearError();
  };

  return (
    <main>
      <h1>Paramètres crypto (avancé)</h1>
      {error !== null && <ErrorDisplay error={error} onDismiss={clearError} />}

      <section aria-labelledby="crypto-settings">
        <h2 id="crypto-settings">Configuration cryptographique</h2>
        <p>
          <strong>Attention:</strong> Modifier ces paramètres peut rendre votre
          wallet incompatible avec les services existants. Utilisez uniquement si
          vous comprenez les implications.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label htmlFor="hash-algo">
              Algorithme de hash
              <select
                id="hash-algo"
                value={settings.hashAlgorithm}
                onChange={(e): void => {
                  setSettings({ ...settings, hashAlgorithm: e.target.value });
                }}
              >
                <option value="sha256">SHA-256</option>
              </select>
            </label>
            <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)' }}>
              Algorithme utilisé pour calculer les hash canoniques des messages.
            </p>
          </div>

          <div>
            <label htmlFor="canon-strict">
              <input
                id="canon-strict"
                type="checkbox"
                checked={settings.jsonCanonizationStrict}
                onChange={(e): void => {
                  setSettings({
                    ...settings,
                    jsonCanonizationStrict: e.target.checked,
                  });
                }}
              />
              Canonisation JSON stricte
            </label>
            <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)' }}>
              Mode strict pour la canonisation JSON (ordre des clés, espaces, etc.).
            </p>
          </div>

          <div>
            <label htmlFor="ecdh-curve">
              Courbe ECDH
              <select
                id="ecdh-curve"
                value={settings.ecdhCurve}
                onChange={(e): void => {
                  setSettings({ ...settings, ecdhCurve: e.target.value });
                }}
                disabled
              >
                <option value="secp256k1">secp256k1</option>
              </select>
            </label>
            <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)' }}>
              Courbe elliptique pour ECDH (non modifiable, secp256k1 uniquement).
            </p>
          </div>

          <div>
            <label htmlFor="nonce-ttl">
              TTL nonce (ms)
              <input
                id="nonce-ttl"
                type="number"
                min="60000"
                max="86400000"
                step="60000"
                value={settings.nonceTtlMs}
                onChange={(e): void => {
                  setSettings({
                    ...settings,
                    nonceTtlMs: parseInt(e.target.value, 10) || DEFAULT_SETTINGS.nonceTtlMs,
                  });
                }}
              />
            </label>
            <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)' }}>
              Durée de vie du cache anti-rejeu pour les nonces (60000-86400000 ms).
            </p>
          </div>

          <div>
            <label htmlFor="timestamp-window">
              Fenêtre timestamp (ms)
              <input
                id="timestamp-window"
                type="number"
                min="60000"
                max="3600000"
                step="60000"
                value={settings.timestampWindowMs}
                onChange={(e): void => {
                  setSettings({
                    ...settings,
                    timestampWindowMs:
                      parseInt(e.target.value, 10) || DEFAULT_SETTINGS.timestampWindowMs,
                  });
                }}
              />
            </label>
            <p style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)' }}>
              Fenêtre de validité pour les timestamps (60000-3600000 ms).
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
          <button type="button" onClick={handleReset}>
            Réinitialiser
          </button>
          <button
            type="button"
            onClick={() => {
              navigate('/');
            }}
          >
            Retour
          </button>
        </div>
      </section>

      <section aria-labelledby="warnings">
        <h2 id="warnings">Avertissements</h2>
        <ul>
          <li>
            <strong>Incompatibilité:</strong> Modifier ces paramètres peut rendre
            votre wallet incompatible avec les services existants.
          </li>
          <li>
            <strong>Version logicielle:</strong> Certains paramètres peuvent ne pas
            être supportés par votre version du wallet.
          </li>
          <li>
            <strong>Recommandation:</strong> Utilisez les valeurs par défaut sauf si
            vous avez une raison spécifique de les modifier.
          </li>
        </ul>
      </section>
    </main>
  );
}
